import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { z } from 'zod'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { PasswordVisibilityIcon } from '@/shared/lib/password-visibility'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { runDetached } from '@/shared/lib/runDetachedPromise'
import { useToast } from '@/shared/toast/ToastProvider'
import { ApiError } from '@/api/http/types'
import { useSessionStore } from '@/stores/useSessionStore'
import { useForceResetStart, useLogin, type ForceResetStartData } from '../api'
import { useClaimPendingSurvey } from '@/features/instrument-onboarding/api/instrument-onboarding-mutations'
import {
  loginSchema,
  type LoginFormData,
  REMEMBERED_EMAIL_KEY,
  PASSWORD_RESET_SUCCESS_STATE_KEY,
  PASSWORD_SETUP_EXPIRED_STATE_KEY,
  NO_PASSWORD_SETUP_STATE_KEY,
  FORGOT_PASSWORD_EMAIL_STATE_KEY,
  readForgotPasswordEmailFromState,
  FORCE_RESET_SESSION_ID_STATE_KEY,
  FORCE_RESET_MASKED_EMAIL_STATE_KEY,
  FORCE_RESET_FROM_STATE_KEY,
  clearForceResetLoginHandoff,
  peekForceResetLoginHandoff,
  resolveOtpSendFailureMessage,
  readWebSurveyOnboardingSession,
  clearWebSurveyOnboardingSession,
} from '../lib'
import {
  SP_FORCE_RESET_LOGIN_SUCCESS_MESSAGE,
  SP_FORGOT_PASSWORD_LOGIN_SUCCESS_MESSAGE,
} from '../lib/password-messages'
import { lockoutFromApiError } from '../lib/lockout'
import { LockoutCountdown } from '../components'
import { useLockoutRemaining } from '../hooks/useLockoutRemaining'
import { errorInputSx, labelSx } from '../styles/authFormStyles'
import {
  authSplitFormBodySx,
  authSplitFormHeaderSx,
  authSplitFormLogoSx,
  authSplitFormPageSx,
  loginFieldErrorSx,
  loginForgotLinkSx,
  loginInputNativeProps,
  loginInputSx,
  loginPrimaryButtonSx,
  loginRememberLabelSx,
  loginRememberRowSx,
  loginServerErrorSx,
  loginWelcomeHeadingSx,
  loginWelcomeSubtitleSx,
} from '../styles/loginSplitStyles'
import {
  authHeadingSlate,
  authInvalidEmailBorder,
  palettePrimaryMain,
} from '@/theme/designTokens'

type LoginStep = 'email' | 'password'

const emailOnlySchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid: Must be a valid email'),
})
type EmailOnlyFormData = z.infer<typeof emailOnlySchema>

function getLoginErrorMessage(error: unknown): string {
  if (
    error instanceof Error &&
    error.message === 'Login session could not be established'
  ) {
    return 'Could not establish a secure session. Please try again.'
  }
  if (!(error instanceof ApiError)) {
    return 'An unexpected error occurred. Please try again.'
  }
  return error.message
}

function emailFieldErrorSx(hasError: boolean) {
  if (!hasError) return {}
  return {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: authInvalidEmailBorder,
    },
  }
}

function renderLoginButtonLabel(isPending: boolean, isLockedOut: boolean) {
  if (isPending) {
    return <CircularProgress size={24} color="inherit" />
  }
  if (isLockedOut) {
    return 'Try again later'
  }
  return 'Login'
}

/** Figma 5074-12188 / 5074-11759 — split-screen Signal Provider login (BFP-264: email-first two-step) */
export function LoginPage() {
  useDocumentTitle('Login')
  const navigate = useNavigate()
  const location = useLocation()
  const login = useLogin()
  const forceResetStart = useForceResetStart()
  const claimPending = useClaimPendingSurvey()
  const { showToast } = useToast()

  const routeState = (location.state as Record<string, unknown> | null) ?? {}
  const forceResetHandoffRef = useRef<ReturnType<
    typeof peekForceResetLoginHandoff
  > | null>(null)
  forceResetHandoffRef.current ??= peekForceResetLoginHandoff()
  const forceResetHandoff = forceResetHandoffRef.current
  const forgotPasswordResetSuccess =
    routeState[PASSWORD_RESET_SUCCESS_STATE_KEY] === true
  const forceResetLoginSuccess = forceResetHandoff.passwordResetSuccess
  const setupExpired = routeState[PASSWORD_SETUP_EXPIRED_STATE_KEY] === true
  const noPasswordSetup = routeState[NO_PASSWORD_SETUP_STATE_KEY] === true

  const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? ''
  const emailFromRoute = readForgotPasswordEmailFromState(location.state)

  const [step, setStep] = useState<LoginStep>('email')
  const [confirmedEmail, setConfirmedEmail] = useState<string>(
    emailFromRoute ?? savedEmail
  )
  const [rememberMe, setRememberMe] = useState(!!savedEmail)
  const [showPassword, setShowPassword] = useState(false)
  const [lockoutUntilMs, setLockoutUntilMs] = useState<number | null>(null)
  const lockoutRemaining = useLockoutRemaining(lockoutUntilMs)
  const isLockedOut = lockoutRemaining > 0

  const forceResetToastShownRef = useRef(false)
  const forgotPasswordToastShownRef = useRef(false)

  useEffect(() => {
    if (!forceResetLoginSuccess || forceResetToastShownRef.current) return
    forceResetToastShownRef.current = true
    clearForceResetLoginHandoff()
    showToast(SP_FORCE_RESET_LOGIN_SUCCESS_MESSAGE, 'success')
  }, [forceResetLoginSuccess, showToast])

  useEffect(() => {
    if (!forgotPasswordResetSuccess || forgotPasswordToastShownRef.current)
      return
    forgotPasswordToastShownRef.current = true
    showToast(SP_FORGOT_PASSWORD_LOGIN_SUCCESS_MESSAGE, 'success')
  }, [forgotPasswordResetSuccess, showToast])

  const emailForm = useForm<EmailOnlyFormData>({
    resolver: zodResolver(emailOnlySchema),
    defaultValues: { email: confirmedEmail },
  })

  const passwordForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: confirmedEmail, password: '' },
  })

  const navigateToForceResetOtp = (result: ForceResetStartData) => {
    const from = (routeState.from ?? forceResetHandoff.from) as
      | { pathname?: string; search?: string }
      | undefined
    navigate('/force-reset/otp', {
      replace: false,
      state: {
        [FORCE_RESET_SESSION_ID_STATE_KEY]: result.sessionId,
        [FORCE_RESET_MASKED_EMAIL_STATE_KEY]: result.maskedEmail,
        [FORCE_RESET_FROM_STATE_KEY]: from ?? null,
      },
    })
  }

  const handleForceResetStartError = (error: unknown) => {
    if (error instanceof ApiError && error.status === 404) {
      emailForm.setError('email', {
        type: 'server',
        message: 'Invalid email or password.',
      })
      return
    }
    showToast(resolveOtpSendFailureMessage(error), 'error')
  }

  const onEmailSubmit = (data: EmailOnlyFormData) => {
    const email = data.email.trim().toLowerCase()
    forceResetStart.mutate(
      { email },
      {
        onSuccess: (result) => {
          if (result.requiresPasswordReset) {
            navigateToForceResetOtp(result)
          } else {
            setConfirmedEmail(email)
            passwordForm.reset({ email, password: '' })
            setStep('password')
          }
        },
        onError: handleForceResetStartError,
      }
    )
  }

  const completeLogin = (email: string, password: string) => {
    login.mutate(
      { email, password, remember_me: rememberMe },
      {
        onSuccess: () => {
          runDetached(
            (async () => {
            posthog.capture('user_logged_in', { remember_me: rememberMe })
            if (rememberMe) {
              localStorage.setItem(REMEMBERED_EMAIL_KEY, email)
            } else {
              localStorage.removeItem(REMEMBERED_EMAIL_KEY)
            }
            // `/me` already ran inside useLogin; read hydrated flags from the store.
            const passwordRequired =
              useSessionStore.getState().passwordSetupRequired === true
            if (passwordRequired) {
              navigate('/set-password', { replace: true })
              return
            }
            const pendingClaim = routeState.pendingSurveyClaim === true
            const pendingSessionId = readWebSurveyOnboardingSession()
            if (pendingClaim && pendingSessionId) {
              try {
                await claimPending.mutateAsync(pendingSessionId)
              } catch {
                /* still open Redeem so the user can sign in to their account */
              }
              clearWebSurveyOnboardingSession()
            }
            const from = (routeState.from ?? forceResetHandoff.from) as
              | { pathname?: string; search?: string }
              | undefined
            const returnPath = pendingClaim
              ? '/rewards?from=signup-survey'
              : from?.pathname == null
                ? '/'
                : `${from.pathname}${from.search ?? ''}`
            navigate(returnPath, {
              replace: true,
              state:
                from?.pathname == null && !pendingClaim
                  ? { loginSuccess: true }
                  : undefined,
            })
            })(),
          )
        },
        onError: (error) => {
          if (error instanceof ApiError) {
            const lockout = lockoutFromApiError(error)
            if (lockout) {
              setLockoutUntilMs(lockout.lockedUntilMs)
              return
            }
            const detail = error.message.toLowerCase()
            if (
              detail.includes('set a password') ||
              detail.includes('invitation')
            ) {
              navigate('/forgot-password', {
                replace: true,
                state: {
                  [FORGOT_PASSWORD_EMAIL_STATE_KEY]: email,
                  [NO_PASSWORD_SETUP_STATE_KEY]: true,
                },
              })
            }
          }
        },
      }
    )
  }

  const onPasswordSubmit = async (data: LoginFormData) => {
    if (isLockedOut) return
    setLockoutUntilMs(null)
    login.reset()

    const email = data.email.trim().toLowerCase()
    const password = data.password
    const emailChanged = email !== confirmedEmail.trim().toLowerCase()

    if (emailChanged) {
      try {
        const result = await forceResetStart.mutateAsync({ email })
        if (result.requiresPasswordReset) {
          navigateToForceResetOtp(result)
          return
        }
        setConfirmedEmail(email)
      } catch (error) {
        handleForceResetStartError(error)
        return
      }
    }

    completeLogin(email, password)
  }

  const isSubmitting = forceResetStart.isPending || login.isPending

  const serverError =
    login.isError && !isLockedOut ? getLoginErrorMessage(login.error) : null

  const passwordStepServerError = serverError

  return (
    <Box data-testid="login-page" sx={authSplitFormPageSx}>
      <Box sx={authSplitFormHeaderSx}>
        <Box
          component="img"
          src={brushfireLogo}
          alt="Brushfire"
          sx={authSplitFormLogoSx}
        />
        <Typography component="h1" sx={loginWelcomeHeadingSx}>
          Welcome
        </Typography>
        <Typography sx={{ ...loginWelcomeSubtitleSx, mt: '16px' }}>
          {step === 'email'
            ? 'Enter your registered email address here.'
            : 'Enter your password to continue.'}
        </Typography>
      </Box>

      <Box sx={authSplitFormBodySx}>
        {setupExpired && (
          <Alert severity="warning" sx={{ mb: 2, width: '100%' }} role="status">
            Your setup session expired. Use Forgot Password to create your
            password and sign in.
          </Alert>
        )}

        {noPasswordSetup && (
          <Alert severity="info" sx={{ mb: 2, width: '100%' }} role="status">
            This account does not have a password yet. Use Forgot Password to
            finish setup.
          </Alert>
        )}

        {lockoutUntilMs && (
          <LockoutCountdown
            lockedUntilMs={lockoutUntilMs}
            message="Too many failed attempts. Please wait 5 minutes before trying again."
          />
        )}

        {/* ── Step 1: Email ─────────────────────────────────── */}
        {step === 'email' && (
          <Box
            component="form"
            noValidate
            onSubmit={emailForm.handleSubmit(onEmailSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            data-testid="login-email-step"
          >
            <Box>
              <Typography
                component="label"
                htmlFor="login-email"
                sx={{ ...labelSx, color: authHeadingSlate }}
              >
                Email
              </Typography>
              <OutlinedInput
                id="login-email"
                data-testid="login-email-field"
                type="email"
                placeholder="Enter your email"
                fullWidth
                autoComplete="email"
                disabled={forceResetStart.isPending}
                error={!!emailForm.formState.errors.email}
                sx={{
                  ...loginInputSx,
                  ...(emailForm.formState.errors.email
                    ? { ...errorInputSx, ...emailFieldErrorSx(true) }
                    : {}),
                }}
                inputProps={loginInputNativeProps}
                {...emailForm.register('email')}
              />
              {emailForm.formState.errors.email && (
                <Typography sx={loginFieldErrorSx}>
                  {emailForm.formState.errors.email.message}
                </Typography>
              )}
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              disabled={forceResetStart.isPending}
              data-testid="login-continue-button"
              sx={loginPrimaryButtonSx}
            >
              {forceResetStart.isPending ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Continue'
              )}
            </Button>
          </Box>
        )}

        {/* ── Step 2: Password ──────────────────────────────── */}
        {step === 'password' && (
          <Box
            component="form"
            noValidate
            onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            data-testid="login-password-step"
          >
            <Box>
              <Typography
                component="label"
                htmlFor="login-email-confirmed"
                sx={{ ...labelSx, color: authHeadingSlate }}
              >
                Email
              </Typography>
              <OutlinedInput
                id="login-email-confirmed"
                data-testid="login-email-confirmed-field"
                type="email"
                placeholder="Enter your email"
                fullWidth
                autoComplete="email"
                disabled={isSubmitting || isLockedOut}
                error={!!passwordForm.formState.errors.email}
                sx={{
                  ...loginInputSx,
                  ...(passwordForm.formState.errors.email
                    ? { ...errorInputSx, ...emailFieldErrorSx(true) }
                    : {}),
                }}
                inputProps={{
                  ...loginInputNativeProps,
                  'data-testid': 'login-email-confirmed',
                }}
                {...passwordForm.register('email')}
              />
              {passwordForm.formState.errors.email && (
                <Typography sx={loginFieldErrorSx}>
                  {passwordForm.formState.errors.email.message}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography
                component="label"
                htmlFor="login-password"
                sx={{ ...labelSx, color: authHeadingSlate }}
              >
                Password
              </Typography>
              <OutlinedInput
                id="login-password"
                data-testid="login-password-field"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                fullWidth
                autoComplete="current-password"
                disabled={isLockedOut || isSubmitting}
                error={!!passwordForm.formState.errors.password}
                sx={{
                  ...loginInputSx,
                  ...(passwordForm.formState.errors.password
                    ? errorInputSx
                    : {}),
                }}
                inputProps={loginInputNativeProps}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      sx={{ color: authHeadingSlate }}
                    >
                      <PasswordVisibilityIcon
                        visible={showPassword}
                        size={20}
                      />
                    </IconButton>
                  </InputAdornment>
                }
                {...passwordForm.register('password')}
              />
              {passwordForm.formState.errors.password && (
                <Typography sx={loginFieldErrorSx}>
                  {passwordForm.formState.errors.password.message}
                </Typography>
              )}

              <Box sx={loginRememberRowSx}>
                <FormControlLabel
                  sx={loginRememberLabelSx}
                  control={
                    <Checkbox
                      size="small"
                      checked={rememberMe}
                      onChange={(_, checked) => setRememberMe(checked)}
                      disabled={isLockedOut}
                      sx={{
                        color: '#49454f',
                        '&.Mui-checked': { color: palettePrimaryMain },
                      }}
                    />
                  }
                  label="Remember me"
                />
                <Button
                  component={RouterLink}
                  to="/forgot-password"
                  variant="text"
                  sx={loginForgotLinkSx}
                >
                  Forgot Password?
                </Button>
              </Box>
            </Box>

            {passwordStepServerError ? (
              <Typography
                data-testid="login-server-error"
                sx={loginServerErrorSx}
              >
                {passwordStepServerError}
              </Typography>
            ) : null}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              disabled={isLockedOut || isSubmitting}
              sx={loginPrimaryButtonSx}
            >
              {renderLoginButtonLabel(isSubmitting, isLockedOut)}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}
